import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import XDateScreen from "../features/home/XDateScreen";
import { useTabSwitch } from "./TabSwitchContext";
import "./MainTabLayer.css";

const ReelsScreen = lazy(() => import("../features/reels/ReelsScreen"));
const ProfileDetails = lazy(() => import("../features/profile/ProfileDetails"));
const UserProfileScreen = lazy(() => import("../features/profile/screens/user-profile/UserProfileScreen"));

const TAB_SLIDE_MS = 880;

const TAB_ORDER = {
  home: 0,
  reels: 1,
  profile: 2,
};

function isProfileExiting(visualTab, exiting, prevTab) {
  if (exiting === "profile") return true;
  return prevTab === "profile" && visualTab !== "profile" && TAB_ORDER[visualTab] < TAB_ORDER.profile;
}

function isPaneShown(tabId, { visualTab, entering, exiting, prevTab, profileUnderlay }) {
  if (visualTab === tabId || entering === tabId || exiting === tabId) {
    return true;
  }

  if (isProfileExiting(visualTab, exiting, prevTab) && tabId === "profile") {
    return true;
  }

  if (isProfileExiting(visualTab, exiting, prevTab) && visualTab === "reels" && tabId === "reels") {
    return true;
  }

  if (visualTab === "profile" && tabId === profileUnderlay) {
    return true;
  }

  if (entering === "reels" && tabId === "home") return true;
  if (entering === "profile" && prevTab === "reels" && tabId === "reels") return true;
  if (entering === "profile" && prevTab === "home" && tabId === "home") return true;

  return false;
}

function getTabSlideClass(tabId, { visualTab, entering, enterReady, exiting, exitReady, profileUnderlay, prevTab }) {
  const profileLeaving = isProfileExiting(visualTab, exiting, prevTab);

  if (profileLeaving && tabId === "profile") {
    return exitReady
      ? "main-tab-layer__slide main-tab-layer__slide--exit"
      : "main-tab-layer__slide main-tab-layer__slide--exit-pending";
  }

  if (exiting === tabId) {
    return exitReady
      ? "main-tab-layer__slide main-tab-layer__slide--exit"
      : "main-tab-layer__slide main-tab-layer__slide--exit-pending";
  }

  if (entering === tabId) {
    return enterReady
      ? "main-tab-layer__slide main-tab-layer__slide--enter"
      : "main-tab-layer__slide main-tab-layer__slide--enter-pending";
  }

  if (
    visualTab === tabId
    || (visualTab === "profile" && tabId === profileUnderlay)
    || (profileLeaving && visualTab === "reels" && tabId === "reels")
  ) {
    return "main-tab-layer__slide main-tab-layer__slide--steady";
  }

  return "main-tab-layer__slide main-tab-layer__slide--hidden";
}

export default function MainTabLayer({ pathname, hidden = false }) {
  const { visualTab } = useTabSwitch();
  const location = useLocation();
  const isUserProfile = pathname.startsWith("/user-profile");
  const skipEntryAnimRef = useRef(Boolean(location.state?.instantTab));

  const homeVisible = visualTab === "home";
  const reelsVisible = visualTab === "reels";
  const profileVisible = visualTab === "profile";

  const [entering, setEntering] = useState(null);
  const [enterReady, setEnterReady] = useState(false);
  const [exiting, setExiting] = useState(null);
  const [exitReady, setExitReady] = useState(false);

  const prevTabRef = useRef(visualTab);
  const [profileUnderlay, setProfileUnderlay] = useState("home");
  const animGenRef = useRef(0);

  const snapProfileToSteady = useCallback(() => {
    if (prevTabRef.current !== "profile") {
      setProfileUnderlay(prevTabRef.current);
    }
    animGenRef.current += 1;
    prevTabRef.current = "profile";
    setEntering(null);
    setEnterReady(false);
    setExiting(null);
    setExitReady(false);
  }, []);

  useLayoutEffect(() => {
    if (!skipEntryAnimRef.current) return;
    skipEntryAnimRef.current = false;
    snapProfileToSteady();
  }, [snapProfileToSteady, visualTab]);

  useLayoutEffect(() => {
    const onProfileRoute = pathname.startsWith("/profile-details");
    if (onProfileRoute && location.state?.instantTab && visualTab === "profile") {
      snapProfileToSteady();
    }
  }, [pathname, location.state, visualTab, snapProfileToSteady]);

  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev === visualTab) return undefined;

    const prevIndex = TAB_ORDER[prev];
    const nextIndex = TAB_ORDER[visualTab];
    const gen = animGenRef.current + 1;
    animGenRef.current = gen;

    let enterTimer = 0;
    let settleTimer = 0;

    const settle = () => {
      if (animGenRef.current !== gen) return;
      if (visualTab === "profile" && prevTabRef.current !== "profile") {
        setProfileUnderlay(prevTabRef.current);
      }
      prevTabRef.current = visualTab;
      setEntering(null);
      setEnterReady(false);
      setExiting(null);
      setExitReady(false);
    };

    if (nextIndex > prevIndex) {
      setExiting(null);
      setExitReady(false);
      setEntering(visualTab);
      setEnterReady(false);

      enterTimer = window.setTimeout(() => {
        if (animGenRef.current !== gen) return;
        setEnterReady(true);
      }, 20);

      settleTimer = window.setTimeout(settle, TAB_SLIDE_MS + 48);
    } else if (nextIndex < prevIndex) {
      const startExit = () => {
        setEntering(null);
        setEnterReady(false);
        setExiting(prev);
        setExitReady(false);
        if (prev === "profile" && (visualTab === "reels" || visualTab === "home")) {
          setProfileUnderlay(visualTab);
        }
      };

      if (prev === "profile") {
        flushSync(startExit);
      } else {
        startExit();
      }

      enterTimer = window.setTimeout(() => {
        if (animGenRef.current !== gen) return;
        setExitReady(true);
      }, 20);

      settleTimer = window.setTimeout(settle, TAB_SLIDE_MS + 48);
    } else {
      prevTabRef.current = visualTab;
      return undefined;
    }

    return () => {
      window.clearTimeout(enterTimer);
      window.clearTimeout(settleTimer);
    };
  }, [visualTab]);

  useEffect(() => {
    if (entering && entering === visualTab && !enterReady) {
      const recoverTimer = window.setTimeout(() => {
        setEnterReady(true);
      }, 48);
      return () => window.clearTimeout(recoverTimer);
    }

    if (exiting && !exitReady) {
      const recoverTimer = window.setTimeout(() => {
        setExitReady(true);
      }, 48);
      return () => window.clearTimeout(recoverTimer);
    }

    if (isProfileExiting(visualTab, exiting, prevTabRef.current) && !exitReady && !exiting) {
      const recoverTimer = window.setTimeout(() => {
        setExiting("profile");
        setExitReady(true);
      }, 48);
      return () => window.clearTimeout(recoverTimer);
    }

    return undefined;
  }, [entering, enterReady, exiting, exitReady, visualTab]);

  const animState = {
    visualTab,
    entering,
    enterReady,
    exiting,
    exitReady,
    prevTab: prevTabRef.current,
    profileUnderlay,
  };
  const homeShown = isPaneShown("home", animState);
  const reelsShown = isPaneShown("reels", animState);
  const profileShown = isPaneShown("profile", animState);

  return (
    <div
      className={`main-tab-layer${hidden ? " main-tab-layer--hidden" : ""}`}
      data-active-tab={visualTab}
      aria-hidden={hidden}
    >
      <div className="main-tab-layer__backdrop" aria-hidden />

      <div
        className={`main-tab-layer__pane main-tab-layer__pane--home${
          homeShown ? " main-tab-layer__pane--on" : ""
        }`}
        aria-hidden={!homeShown}
      >
        <XDateScreen isTabActive={homeVisible} />
      </div>

      <div
        className={`main-tab-layer__pane main-tab-layer__pane--reels${
          reelsShown ? " main-tab-layer__pane--on" : ""
        }`}
        aria-hidden={!reelsShown}
      >
        <div className={getTabSlideClass("reels", animState)}>
          <Suspense fallback={null}>
            <ReelsScreen isTabActive={reelsVisible} />
          </Suspense>
        </div>
      </div>

      <div
        className={`main-tab-layer__pane main-tab-layer__pane--profile${
          profileShown ? " main-tab-layer__pane--on" : ""
        }`}
        aria-hidden={!profileShown}
      >
        <div className={getTabSlideClass("profile", animState)}>
          <Suspense fallback={null}>
            {isUserProfile ? <UserProfileScreen /> : <ProfileDetails />}
          </Suspense>
        </div>
      </div>
    </div>
  );
}
